import { AuthenticationResult, PublicClientApplication } from "@azure/msal-browser";
import { envVariable, Variable } from "../constants/envVariable";
import { captureError } from "../helpers/sentryHelper";
import { MsalTokenCredential } from "./msalTokenCredential";
import { MsalAuthError } from "../acs/error";

let msalInitialized = false;

export const msalConfig = {
	auth: {
		clientId: envVariable(Variable.AzureClients)["unifyClientId"],
		redirectUri: window.location.origin,
		postLogoutRedirectUri: "/",
		authority: "https://login.microsoftonline.com/common",
	},
	cache: {
		cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
	}
};

export const unifyAuthScopes = [
	'https://auth.msft.communication.azure.com/TeamsExtension.ManageCalls',
	'https://auth.msft.communication.azure.com/Teams.ManageCalls',
	'https://auth.msft.communication.azure.com/Teams.ManageChats'
];

export const teamsUserRequest = {
	scopes: unifyAuthScopes
};

export const MsalInstance = new PublicClientApplication(msalConfig);

export const ensureMsalInitialized = async () => {
	if (!msalInitialized) {
		await MsalInstance.initialize();
		msalInitialized = true;
	}
};

// Utility to detect if code runs inside a popup or iframe
const isInPopupOrIframe = (): boolean => {
	return !!(window.opener || window.parent !== window);
};

const acquireTokenWithPopup = async (
	request: any
): Promise<AuthenticationResult | undefined> => {
	const popupResult = await MsalInstance.acquireTokenPopup(request);
	if (popupResult.account) {
		MsalInstance.setActiveAccount(popupResult.account);
	}

	return popupResult;
}

const acquireMsalToken = async (
	request: any
): Promise<AuthenticationResult | undefined> => {
	await ensureMsalInitialized();

	const account = MsalInstance.getActiveAccount();
	request.account = account;
	request.forceRefresh = false;

	try {
		// No active account → interactive sign-in
		if (!account) {
			if (isInPopupOrIframe()) {
				console.warn("acquireMsalToken: Skipping popup inside popup/iframe context.");

				return undefined;
			}

			return await acquireTokenWithPopup(request);
		}

		// Try silent first
		const silentResult = await MsalInstance.acquireTokenSilent(request);
		if (silentResult?.accessToken) {
			return silentResult;
		}

		// Silent failed → fallback to popup
		if (isInPopupOrIframe()) {
			console.warn("acquireMsalToken: Silent failed, popup blocked in popup/iframe context.");

			return undefined;
		}

		return await acquireTokenWithPopup(request);
	} catch (error: any) {
		captureError(error, "acquireMsalToken: Token acquisition failed, retrying with popup.");

		if (isInPopupOrIframe()) {
			console.warn("acquireMsalToken: Popup retry blocked inside popup/iframe.");

			return undefined;
		}

		try {
			return await acquireTokenWithPopup(request);
		} catch (interactiveError: any) {
			captureError(interactiveError, "acquireMsalToken: Interactive token acquisition fails.");

			return undefined;
		}
	}
};

export const authenticateMsal = async (
	request: any
): Promise<AuthenticationResult | undefined> => {
	return acquireMsalToken(request);
};

export const getMsalTokenCredential = (): MsalTokenCredential => {
	const getTokenFn = async (): Promise<string> => {
		const result = await acquireMsalToken(teamsUserRequest);

		if (!result?.accessToken) {
			throw new MsalAuthError("Failed to acquire MSAL access token.");
		}

		return result.accessToken;
	};

	return new MsalTokenCredential(getTokenFn);
};